import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    this.#initLoadMore();
    this.#initScrollMemory();
  }

  /**
   * Récupère une page de la collection et en extrait les cartes produit.
   *
   * @param {string} url
   * @returns {Promise<{ items: Element[], next: string | undefined }>}
   */
  async #fetchPageItems(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const dom = new DOMParser().parseFromString(await response.text(), 'text/html');
    const items = Array.from(dom.querySelectorAll('[ref="grid"] .product-grid__item'));
    const nextButton = dom.querySelector('[data-load-more]');

    return { items, next: nextButton instanceof HTMLElement ? nextButton.dataset.url : undefined };
  }

  /**
   * Bouton « Charger plus » : ajoute la page suivante à la liste et tient l'adresse à jour (?page=N, sans nouvelle entrée d'historique).
   */
  #initLoadMore() {
    const loadMoreBtn = this.querySelector('[data-load-more]');
    if (!(loadMoreBtn instanceof HTMLElement)) return;

    let loading = false;

    loadMoreBtn.addEventListener('click', async (event) => {
      event.preventDefault();

      const pageURL = loadMoreBtn.dataset.url;
      const grid = this.querySelector('[ref="grid"]');
      if (loading || !pageURL || !grid) return;

      loading = true;
      loadMoreBtn.setAttribute('aria-disabled', 'true');
      loadMoreBtn.textContent = 'Chargement…';

      try {
        const { items, next } = await this.#fetchPageItems(pageURL);
        grid.append(...items);

        const loadedPage = new URL(pageURL, window.location.origin).searchParams.get('page');
        if (loadedPage) {
          const newURL = new URL(window.location.href);
          newURL.searchParams.set('page', loadedPage);
          history.replaceState(history.state, '', newURL.toString());
        }

        if (next) {
          loadMoreBtn.dataset.url = next;
          loadMoreBtn.setAttribute('href', next);
          loadMoreBtn.textContent = 'Charger plus';
          loadMoreBtn.removeAttribute('aria-disabled');
        } else {
          loadMoreBtn.closest('.load-more-container')?.remove();
        }
      } catch (error) {
        console.error('Erreur lors du chargement des produits :', error);
        loadMoreBtn.textContent = 'Erreur, réessayer';
        loadMoreBtn.removeAttribute('aria-disabled');
      } finally {
        loading = false;
      }
    });
  }

  /**
   * Retour depuis une fiche produit : recharge les pages précédentes (1 à N-1) au-dessus de la page N de l'adresse,
   * puis remet la page exactement à la position où se trouvait la carte cliquée.
   */
  #initScrollMemory() {
    const storageKey = 'kl-collection-scroll';
    const grid = this.querySelector('[ref="grid"]');
    if (!grid) return;

    /** @type {{ id: string, offset: number } | null} */
    let lastClick = null;

    grid.addEventListener('click', (event) => {
      const card = event.target instanceof Element ? event.target.closest('.product-grid__item') : null;
      if (!card || !card.id) return;
      lastClick = { id: card.id, offset: card.getBoundingClientRect().top };
    });

    window.addEventListener('pagehide', () => {
      if (!lastClick) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({ path: window.location.pathname, ...lastClick }));
      } catch {
        // stockage indisponible : on garde le comportement du navigateur
      }
    });

    const navigation = performance.getEntriesByType('navigation')[0];
    if (!navigation || /** @type {PerformanceNavigationTiming} */ (navigation).type !== 'back_forward') return;

    /** @type {{ path: string, id: string, offset: number } | null} */
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    } catch {
      saved = null;
    }
    if (!saved || saved.path !== window.location.pathname) return;

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    const pageNumber = Number(new URL(window.location.href).searchParams.get('page') || 1);
    this.setAttribute('data-restoring', '');
    const safety = setTimeout(() => this.removeAttribute('data-restoring'), 5000);

    (async () => {
      try {
        if (pageNumber > 1) {
          const urls = Array.from({ length: pageNumber - 1 }, (_, index) => {
            const url = new URL(window.location.href);
            url.searchParams.set('page', String(index + 1));
            url.hash = '';
            return url.toString();
          });
          const pages = await Promise.all(urls.map((url) => this.#fetchPageItems(url)));
          grid.prepend(...pages.flatMap((page) => page.items));
        }

        const target = saved && document.getElementById(saved.id);
        if (target && saved) {
          const top = target.getBoundingClientRect().top + window.scrollY - saved.offset;
          window.scrollTo({ top, behavior: 'instant' });
        }
      } catch (error) {
        console.error('Impossible de restaurer la liste de produits :', error);
      } finally {
        clearTimeout(safety);
        this.removeAttribute('data-restoring');
      }
    })();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
